import type { ContentCollection, SourceConfig } from './contracts'

export const MAX_ENABLED_SOURCES = 10

function text(value: unknown): string {
    return typeof value === 'string' ? value.trim() : ''
}

function normalizeSource(value: unknown, index: number): SourceConfig | undefined {
    if (!value || typeof value !== 'object') { return undefined }
    const raw = value as Partial<SourceConfig>
    const id = text(raw.id)
    const name = text(raw.name)
    const apiUrl = text(raw.apiUrl)
    if (!id || !name || !/^https?:\/\//iu.test(apiUrl)) { return undefined }
    return {
        id,
        name,
        apiUrl,
        enabled: raw.enabled !== false,
        sortOrder: Number.isFinite(raw.sortOrder) ? Number(raw.sortOrder) : index,
        searchable: raw.searchable !== false,
        ...(text(raw.detailUrl) ? { detailUrl: text(raw.detailUrl) } : {}),
        ...(raw.headers && typeof raw.headers === 'object' ? { headers: raw.headers } : {}),
    }
}

export function normalizeCollection(value: unknown): ContentCollection | undefined {
    if (!value || typeof value !== 'object') { return undefined }
    const raw = value as Partial<ContentCollection>
    const id = text(raw.id)
    const name = text(raw.name)
    const url = text(raw.url)
    if (!id || !name || !url) { return undefined }
    const vodSources = Array.isArray(raw.vodSources)
        ? raw.vodSources.flatMap((source, index) => normalizeSource(source, index) ?? [])
        : []
    return {
        id,
        name,
        url,
        enabled: raw.enabled !== false,
        updatedAt: Number.isFinite(raw.updatedAt) ? Number(raw.updatedAt) : Date.now(),
        vodSources,
    }
}

export function normalizeCollections(value: unknown): ContentCollection[] {
    if (!Array.isArray(value)) { return [] }
    return value.flatMap(collection => normalizeCollection(collection) ?? [])
}

export function enabledSourceCount(collections: ContentCollection[]): number {
    return collections.reduce((total, collection) => total + (collection.enabled
        ? collection.vodSources.filter(source => source.enabled).length
        : 0), 0)
}

export function applySourceCapacity(collection: ContentCollection, capacity: number): ContentCollection {
    let remaining = Math.max(0, capacity)
    return {
        ...collection,
        vodSources: collection.vodSources.map((source) => {
            if (!source.enabled) { return source }
            const enabled = remaining > 0
            if (enabled) { remaining-- }
            return { ...source, enabled }
        }),
    }
}

export function enforceSourceCapacity(collections: ContentCollection[]): ContentCollection[] {
    let remaining = MAX_ENABLED_SOURCES
    return collections.map((collection) => {
        if (!collection.enabled) { return collection }
        const normalized = applySourceCapacity(collection, remaining)
        remaining -= normalized.vodSources.filter(source => source.enabled).length
        return normalized
    })
}
